import {
  ChatSender,
  CustomerChatHistoryConnection,
  CustomerChatHistoryEdge,
  CustomerChatMessage,
  CustomerChatSessionResultTypenames,
  PageInfo,
  Typenames,
} from '@varo/graphql-schema-types';
import { useFocusEffect } from 'expo-router/react-navigation';
import {
  CHAT_START_MSG,
  getIsTransient,
  getUserChoiceMessage,
} from 'features/chat/helpers/ChatEvent.helper';
import { ChatContext } from 'features/chat/providers/ChatContextProvider';
import { useCallback, useContext, useEffect, useState } from 'react';
import { AppState } from 'react-native';
import ChatHistoryQuery from 'services/graphql/queries/ChatHistory.query';
import { logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';
import { useQuery } from 'urql';

const useChatHistory = () => {
  const {
    setChatOutageMessage,
    chatSessionId,
    history,
    setHistory,
    setChatSessionId,
    setIsChatStarted,
    isChatStarted,
    setIsChattingWithBot,
    isChatEndedFromEvent,
  } = useContext(ChatContext);

  const [pageInfo, setPageInfo] = useState<PageInfo>();
  const [endMessageId, setEndMessageId] = useState<string | undefined>(
    undefined,
  );
  const [
    { data, fetching: historyConnectionFetching, error: historyFetchingError },
    reExecuteQuery,
  ] = useQuery<{
    customerChatHistory: CustomerChatHistoryConnection;
  }>({
    query: ChatHistoryQuery,
    requestPolicy: 'network-only',
    variables: { after: endMessageId, first: 20 },
    pause: true,
  });

  useEffect(() => {
    reExecuteQuery({ requestPolicy: 'network-only' });
  }, [reExecuteQuery, endMessageId]);

  useEffect(() => {
    const handleAppStateChange = (nextAppState: string) => {
      if (nextAppState === 'active') {
        setEndMessageId(undefined);
        setHistory(() => []);
        reExecuteQuery({ requestPolicy: 'network-only' });
      }
    };

    const subscription = AppState.addEventListener(
      'change',
      handleAppStateChange,
    );

    return () => {
      subscription.remove();
    };
  }, [reExecuteQuery, setHistory]);

  useFocusEffect(
    useCallback(() => {
      reExecuteQuery({ requestPolicy: 'network-only' });
    }, [reExecuteQuery]),
  );

  const isChatStartMessage = (node: CustomerChatMessage): boolean => {
    if (node.content && 'html' in node.content) {
      return (node?.content?.html || '').includes(CHAT_START_MSG);
    }
    return (
      (node?.content?.__typename === Typenames.ChatContentMessage &&
        node?.content?.message) ||
      ''
    ).includes(CHAT_START_MSG);
  };

  // Function to add a flag for chat start messages
  const addStartChatStartedFlagsToMessages = (
    edge: CustomerChatHistoryEdge,
  ): CustomerChatMessage => {
    const node = edge.node;
    if (isChatStartMessage(node)) {
      return { ...node, sender: ChatSender.UndefinedChatSender };
    }
    return node;
  };

  // Function to filter out transient or user choice messages
  const removeInfoMarkerMessages = (
    chatEvent: CustomerChatMessage,
  ): boolean => {
    const isTransient = getIsTransient(chatEvent);
    const userChoiceMessage = getUserChoiceMessage(chatEvent);
    return !(isTransient || userChoiceMessage);
  };

  useEffect(() => {
    if (data) {
      const {
        edges,
        pageInfo: nextPage,
        chatOutageMessage,
        chatSession,
      } = data.customerChatHistory;
      if (chatOutageMessage) {
        setChatOutageMessage(chatOutageMessage);
      }
      // Only restore session state if the chat wasn't intentionally ended.
      // isChatEndedFromEvent is set when user leaves chat or session ends via event.
      if (
        chatSession?.__typename ===
          CustomerChatSessionResultTypenames.CustomerChatSessionSuccess &&
        !isChatEndedFromEvent
      ) {
        setChatSessionId(chatSession.sessionId);
        setIsChatStarted(true);
        setIsChattingWithBot(!chatSession.isConnectedToAnAgent);
      }
      if (
        chatSession?.__typename ===
        CustomerChatSessionResultTypenames.CustomerChatSessionError
      ) {
        logErrorWithContext(`ChatHistory error`, {
          featureArea: FeatureArea.CUSTOMER_SERVICING_SUPPORT,
          responsibleTeam: ResponsibleTeam.CUSTOMER_SUPPORT,
          severity: ErrorSeverity.LOW,
        });
      }

      const respHistory = edges
        .map(addStartChatStartedFlagsToMessages)
        .filter(removeInfoMarkerMessages);

      setHistory((prev) => {
        const seenIds = new Set(prev.map((msg) => msg.id));
        const deduplicated = respHistory.filter((msg) => !seenIds.has(msg.id));
        return [...prev, ...deduplicated];
      });

      setPageInfo(nextPage);
    } else {
      logErrorWithContext(`ChatHistory error`, {
        featureArea: FeatureArea.CUSTOMER_SERVICING_SUPPORT,
        responsibleTeam: ResponsibleTeam.CUSTOMER_SUPPORT,
        severity: ErrorSeverity.LOW,
      });
    }
    //Disabling the eslint as it goes in infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return {
    history: history.slice(),
    reExecuteQuery,
    fetchHistory: () => {
      const shouldLoadMore =
        !historyConnectionFetching && pageInfo?.hasNextPage;

      if (shouldLoadMore && pageInfo?.endCursor !== endMessageId) {
        setEndMessageId(pageInfo.endCursor ?? undefined);
        return true;
      }

      return false;
    },
    historyConnectionFetching,
    historyFetchingError,
    pageInfo,
    chatSessionId,
    isChatStarted,
  };
};

export default useChatHistory;
